import React from "react";
import { Link } from "react-router-dom";
import Footer from "./Footer";
import Navbar from "./Navbar";
import { FaCheckCircle, FaShieldAlt } from "react-icons/fa";
import WhyChooseUs from "./WhyChooseUs";
// import CheckEligibility from "./CheckEligibility";

// Premium Assets
import collaboration_indian_professionals from "../assets/premium/collaboration_indian_professionals.png";
import leftwhychooseimagerighttopicon from "../assets/whychooseus/leftwhychooseimagerighttopicon.png";

const AboutUs: React.FC = () => {
  const companyName = (import.meta.env.VITE_COMPANY_NAME || "Dezire Loan").trim();

  return (
    <div className="min-h-screen w-full bg-[#D6EDFE] text-[var(--text-primary)] relative overflow-x-hidden font-sans selection:bg-[var(--primary)] selection:text-white">
      <div className="relative z-10 flex w-full flex-col items-center">
        <Navbar />

        {/* About Hero Section: Clean layout matching WhyChooseUs UI design */}
        <section className="w-full bg-[#FFFDFB] pt-32 pb-16 px-6 lg:px-20 flex justify-center overflow-hidden border-b border-[#FBE3DF]">
          <div className="max-w-[1400px] w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Content Column (7 cols) */}
            <div className="col-span-1 lg:col-span-7 flex flex-col items-center lg:items-start w-full text-center lg:text-left">
              
              {/* Header Badge */}
              <div className="flex items-center gap-3 text-neutral-500 uppercase tracking-[0.15em] text-xs md:text-sm font-bold mb-3">
                <span className="w-7 h-[1.5px] bg-neutral-400" />
                <span>About {companyName}</span>
              </div>

              {/* Heading */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[50px] font-black leading-[1.15] tracking-tight text-neutral-900 mt-2">
                Empowering professionals with 
                <span className="block text-[#DE3226] mt-2">instant salary access.</span>
              </h1>

              {/* Description Paragraphs */}
              <div className="space-y-5 mt-6 max-w-xl text-neutral-600 font-medium text-sm sm:text-base leading-relaxed">
                <p className="text-neutral-800 font-extrabold text-base sm:text-lg leading-snug">
                  We're bridging the payday gap so you don't have to wait for what you've already earned. 💸
                </p>
                <p>
                  Life doesn't wait for your salary cycle. Whether it's unexpected medical bills, family needs, or urgent home repairs, we make salary access fast, fair, and fully transparent.
                </p>
                <p>
                  By leveraging state-of-the-art secure digital processing, we provide modern salary advances directly to your bank account with absolutely zero hidden fees.
                </p>
              </div>

              {/* Apply CTA Button */}
              <Link
                to="/apply-now"
                className="bg-[#DE3226] hover:bg-[#A82A1B] text-white px-8 py-3.5 rounded-[12px] font-bold text-base mt-8 shadow-sm active:scale-95 transition-all w-fit"
              >
                Apply Now
              </Link>

            </div>

            {/* Right Image Column (5 cols) */}
            <div className="col-span-1 lg:col-span-5 w-full flex justify-center relative">
              <div className="relative w-full max-w-[480px] lg:max-w-none h-[320px] sm:h-[420px] lg:h-[500px] rounded-[24px] md:rounded-[32px] overflow-hidden shadow-sm border border-neutral-100">
                <img
                  src={collaboration_indian_professionals}
                  alt="Dezire Loan Professional Team Collaboration"
                  className="w-full h-full object-cover select-none pointer-events-none"
                />
                
                {/* Red logo overlay icon at top left (WhyChooseUs Ref) */}
                <img
                  src={leftwhychooseimagerighttopicon}
                  alt="Dezire Loan Brand Icon"
                  className="absolute top-6 left-6 w-16 h-16 object-contain z-10 pointer-events-none select-none"
                />
              </div>

              {/* Floating Stats Card (WhyChooseUs Red Ref) */}
              <div className="absolute -bottom-4 -left-4 bg-[#DE3226] py-3.5 px-5 rounded-[16px] shadow-md border border-[#FBE3DF]/30 hidden sm:block animate-bounce-slow z-20">
                <div className="text-white text-2xl font-black mb-0.5">100%</div>
                <div className="text-white/80 font-bold text-[9px] uppercase tracking-widest leading-none">Secure & Fair</div>
              </div>
            </div>

          </div>
        </section>

        {/* Mission & Vision Section - Premium Top-Accented Cards */}
        <section className="w-full py-16 sm:py-20 px-6 lg:px-20 bg-white relative z-10">
          <div className="max-w-[1300px] mx-auto w-full grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "Our Mission",
                desc: "To provide every working professional with immediate, fair, and responsible access to their earned income. We're leveraging technology to end the stress of the payday gap.",
                icon: <FaShieldAlt className="w-5 h-5" />,
                watermark: "M"
              },
              {
                title: "Our Vision",
                desc: "To become the most trusted financial companion for modern employees, building a future where your hard-earned money is always just a tap away.",
                icon: <FaCheckCircle className="w-5 h-5" />,
                watermark: "V"
              }
            ].map((item, idx) => (
              <div
                key={idx}
                className="group relative p-8 rounded-[24px] border border-slate-100/80 shadow-[0_10px_25px_rgba(0,0,0,0.02)] transition-all duration-500 overflow-hidden bg-white text-left flex flex-col justify-between gap-6"
              >
                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[4px] bg-[#DE3226]" />
                
                {/* Background Letter Watermark */}
                <div className="absolute right-[5px] bottom-[-25px] text-slate-100/[0.12] text-[150px] font-black pointer-events-none select-none rotate-[8deg] z-0 leading-none group-hover:text-slate-100/[0.22] transition-colors">
                  {item.watermark}
                </div>

                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-[#DE3226]/30 flex items-center justify-center mb-6 text-[#DE3226] shadow-sm">
                    {item.icon}
                  </div>

                  <h2 className="text-xl sm:text-2xl font-bold text-[#0F172A] mb-3 tracking-tight">
                    {item.title}
                  </h2>
                  <p className="text-slate-500 text-[14px] sm:text-[15px] font-medium leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reusing Premium Sections */}
        <div className="w-full bg-white relative z-10 flex flex-col items-center">
          <WhyChooseUs />
          {/* <CheckEligibility /> */}
        </div>

        <Footer />
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 4s ease-in-out infinite;
        }
      `}} />
    </div>
  );
};

export default AboutUs;
